/**
 * App Builder -lausekekieli: tokenisoija + Pratt-parseri + evaluaattori
 * (AB-WP2.1; portattu fasthub-agentin AppRuntimeScripts.java:n
 * reaktiivisesta runtimesta — mutta OIKEANA TS-modulina, ei JS:nä
 * Java-stringeissä).
 *
 * Tämä moduuli on kieliopin AINOA toteutus: runtime evaluoi sillä,
 * editori (AB-WP3.x) validoi sillä, ja WP2.2:n kielioppikorpus ajaa sitä
 * vitestissä. Lähteen "kaksi käsin synkattua toteutusta ilman
 * conformance-testiä" -ongelma on poistettu rakenteella, ei kurilla.
 *
 * PAKOLLISET turvakorjaukset lähteen todennettuun sandbox-pakoon
 * (`x.constructor.constructor('return 42')()` ajoi mielivaltaista JS:ää):
 *  1. SAFE_METHODS-portti käyttää omaa avainta (Set), EI totuusarvo-hakua
 *     objektiliteraalista, jonka prototyyppiketju vuoti 'constructor'in.
 *  2. Prototyyppiominaisuudet (constructor/__proto__/prototype) on
 *     estetty evaluaattorissa KAIKISSA property-poluissa (member, index,
 *     metodikutsu) — myös laskettu avain `x['const'+'ructor']` palauttaa
 *     undefined.
 *  3. Parseri hylkää kielletyt tunnisteet jo jäsennysvaiheessa
 *     (member-accessin propina tai paljaana tunnisteena), jolloin editori
 *     ja palvelinpuolen lint (AppContentValidator) näkevät saman hylkäyksen.
 * Regressiotestit ajetaan oikeassa JS-moottorissa (vitest + korpus).
 */

import {
  addDaysTo,
  daysBetweenSigned,
  formatDateLocalized,
  nowLocal,
  todayLocal,
} from './dateFunctions';

export type Token =
  | { type: 'num'; value: number }
  | { type: 'str'; value: string }
  | { type: 'bool'; value: boolean }
  | { type: 'null' }
  | { type: 'id'; value: string }
  | { type: 'op'; value: string };

export type AstNode =
  | { kind: 'lit'; value: unknown }
  | { kind: 'id'; name: string }
  | { kind: 'member'; obj: AstNode; prop: string }
  | { kind: 'index'; obj: AstNode; key: AstNode }
  | { kind: 'call'; callee: AstNode; args: AstNode[] }
  | { kind: 'un'; op: '!' | '-'; arg: AstNode }
  | { kind: 'bin'; op: string; left: AstNode; right: AstNode }
  | { kind: 'tern'; cond: AstNode; then: AstNode; els: AstNode }
  | { kind: 'array'; elements: AstNode[] }
  | { kind: 'object'; pairs: { key: string; value: AstNode }[] }
  | { kind: 'arrow'; params: string[]; body: AstNode };

/** Evaluointitila: komponentti-id / nimiavaruus → arvo(objekti). */
export type ExprState = Record<string, unknown>;

/**
 * Prototyyppiketjun tunnisteet joihin lauseke ei saa koskea missään
 * property-positiossa. Sama lista vartioi parseria, evaluaattoria ja
 * palvelinpuolen linttiä (AppContentValidator peilaa tämän — pidä synkassa;
 * WP2.2-korpuksen security-tapaukset pinnaavat molemmat päät).
 */
export const FORBIDDEN_IDENTIFIERS: ReadonlySet<string> =
  new Set(['constructor', '__proto__', 'prototype']);

// ---------- Tokenisoija ----------

export function tokenize(src: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const ch = src.charAt(i);
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i++;
      continue;
    }
    if (ch >= '0' && ch <= '9') {
      const start = i;
      while (i < src.length && ((src.charAt(i) >= '0' && src.charAt(i) <= '9') || src.charAt(i) === '.')) i++;
      tokens.push({ type: 'num', value: parseFloat(src.substring(start, i)) });
      continue;
    }
    if (ch === '"' || ch === "'") {
      const quote = ch;
      i++;
      let s = '';
      let closed = false;
      while (i < src.length) {
        if (src.charAt(i) === quote) {
          closed = true;
          break;
        }
        if (src.charAt(i) === '\\' && i + 1 < src.length) {
          s += src.charAt(i + 1);
          i += 2;
        } else {
          s += src.charAt(i);
          i++;
        }
      }
      if (!closed) throw new Error('Unterminated string literal');
      i++; // sulkeva lainausmerkki
      tokens.push({ type: 'str', value: s });
      continue;
    }
    if ((ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z') || ch === '_' || ch === '$') {
      const idStart = i;
      while (i < src.length) {
        const c2 = src.charAt(i);
        if ((c2 >= 'a' && c2 <= 'z') || (c2 >= 'A' && c2 <= 'Z')
          || (c2 >= '0' && c2 <= '9') || c2 === '_' || c2 === '-' || c2 === '$') {
          i++;
        } else break;
      }
      const word = src.substring(idStart, i);
      if (word === 'true') tokens.push({ type: 'bool', value: true });
      else if (word === 'false') tokens.push({ type: 'bool', value: false });
      else if (word === 'null') tokens.push({ type: 'null' });
      else tokens.push({ type: 'id', value: word });
      continue;
    }
    // Kolmimerkkiset operaattorit ensin, ettei === / !== leikkaudu.
    // Strikti muoto normalisoituu == / != -muotoon tokenisoinnissa —
    // evaluaattori laskee == :n joka tapauksessa striktinä (l === r).
    const three = src.substring(i, i + 3);
    if (three === '===' || three === '!==') {
      tokens.push({ type: 'op', value: three === '===' ? '==' : '!=' });
      i += 3;
      continue;
    }
    const two = src.substring(i, i + 2);
    if (two === '==' || two === '!=' || two === '<=' || two === '>='
      || two === '&&' || two === '||' || two === '=>') {
      tokens.push({ type: 'op', value: two });
      i += 2;
      continue;
    }
    if ('.,()[]{}<>+-*/%!?:'.indexOf(ch) !== -1) {
      tokens.push({ type: 'op', value: ch });
      i++;
      continue;
    }
    // Tuntematon merkki on virhe. (Lähde skippasi hiljaa "spike-grade
    // tolerance" -perusteella — strikti hylkäys on parempi editori- ja
    // palvelinvalidoinnille: roskasyöte ei saa parse:autua "melkein".)
    throw new Error(`Unexpected character '${ch}' at position ${i}`);
  }
  return tokens;
}

// ---------- Parseri (Pratt, presedenssi) ----------
// ternary < || < && < eq < cmp < add < mul < unary < member/call < primary.

export function parse(src: string): AstNode {
  const tokens = tokenize(src);
  let pos = 0;

  const peek = () => tokens[pos];
  function eat(type: Token['type'], value?: string): Token | null {
    const t = tokens[pos];
    if (!t || t.type !== type) return null;
    if (value !== undefined && (t as { value?: unknown }).value !== value) return null;
    pos++;
    return t;
  }
  function expect(type: Token['type'], value?: string): Token {
    const t = eat(type, value);
    if (!t) {
      throw new Error(`Parse error at position ${pos} (expected ${type}${value !== undefined ? ` "${value}"` : ''})`);
    }
    return t;
  }
  /** Turvakorjaus 3: kielletty tunniste on jäsennysvirhe. */
  function guardIdentifier(name: string): string {
    if (FORBIDDEN_IDENTIFIERS.has(name)) {
      throw new Error(`Forbidden identifier '${name}' in expression`);
    }
    return name;
  }

  /**
   * Sulkullinen parametrilista vai ryhmittely? Ennakkokatsaus, joka EI kuluta
   * tokeneita: skannaa `(`:aa vastaavan `)`:n tasapainotetusti ja hyväksyy
   * arrow'ksi vain jos (a) sitä seuraa `=>` ja (b) sisältö on pilkulla eroteltu
   * lista pelkkiä tunnisteita (myös tyhjä lista kelpaa, `D2` = A).
   *
   * <p>Ehto (b) on se, joka pitää ryhmittelyn koskemattomana: `(a + b) * c` ja
   * `(a)` päätyvät aina vanhaan polkuun, koska ne eivät ole pelkkiä
   * id/pilkku-jonoja. Kaikki mitä tämä ei tunnista — oletusarvot,
   * destrukturointi, rest — putoaa ryhmittelyyn ja tuottaa saman
   * jäsennysvirheen kuin ennenkin, joten kieli ei kasva vahingossa.
   *
   * @returns parametrinimet ja `)`:n jälkeisen `=>`:n jälkeinen tokenindeksi,
   *          tai null jos kyseessä ei ole parametrilista
   */
  function tryScanArrowParams(): { params: string[]; nextPos: number } | null {
    const open = tokens[pos];
    if (!open || open.type !== 'op' || open.value !== '(') return null;

    let depth = 0;
    let close = -1;
    for (let i = pos; i < tokens.length; i++) {
      const tok = tokens[i];
      if (tok.type !== 'op') continue;
      if (tok.value === '(' || tok.value === '[' || tok.value === '{') depth++;
      else if (tok.value === ')' || tok.value === ']' || tok.value === '}') {
        depth--;
        if (depth === 0) {
          close = i;
          break;
        }
      }
    }
    // Sulkematon sulku (tai väärä sulkija): anna varsinaisen jäsennyksen
    // tuottaa virhe oikeasta kohdasta sen sijaan että arvattaisiin täällä.
    if (close < 0) return null;
    const closer = tokens[close];
    if (closer.type !== 'op' || closer.value !== ')') return null;

    const after = tokens[close + 1];
    if (!after || after.type !== 'op' || after.value !== '=>') return null;

    // Sisältö on tokenit pos+1 … close-1: n parametria = n tunnistetta +
    // (n-1) pilkkua, eli pariton määrä — paitsi tyhjä lista (0). Parillinen
    // määrä tarkoittaa perässä roikkuvaa pilkkua `(a,)`, joka hylätään
    // ennemmin kuin arvataan.
    const tokenCount = close - pos - 1;
    if (tokenCount > 0 && tokenCount % 2 === 0) return null;

    const params: string[] = [];
    for (let i = pos + 1; i < close; i++) {
      const tok = tokens[i];
      const expectIdentifier = (i - pos) % 2 === 1;
      if (expectIdentifier) {
        if (tok.type !== 'id') return null;
        params.push(tok.value);
      } else if (!(tok.type === 'op' && tok.value === ',')) {
        return null;
      }
    }

    return { params, nextPos: close + 2 };
  }

  function parseExpr(): AstNode {
    // Yhden parametrin arrow ilman sulkeita (x => body).
    const t = peek();
    const next = tokens[pos + 1];
    if (t && t.type === 'id' && next && next.type === 'op' && next.value === '=>') {
      const param = guardIdentifier((tokens[pos] as { value: string }).value);
      pos += 2; // id + =>
      const body = parseExpr();
      return { kind: 'arrow', params: [param], body };
    }
    // Sulkullinen parametrilista ((a, b) => body). Tämä on se muoto jota
    // reduce vaatii: reduce((summa, rivi) => summa + rivi.n, 0).
    const scanned = tryScanArrowParams();
    if (scanned) {
      const params = scanned.params.map(guardIdentifier);
      // Duplikaatti varjostaisi hiljaa (`D3` = A): aina kirjoitusvirhe.
      const unique = new Set(params);
      if (unique.size !== params.length) {
        throw new Error('Duplicate arrow parameter in expression');
      }
      pos = scanned.nextPos;
      const body = parseExpr();
      return { kind: 'arrow', params, body };
    }
    return parseTernary();
  }
  function parseTernary(): AstNode {
    const cond = parseOr();
    if (eat('op', '?')) {
      const thenE = parseExpr();
      expect('op', ':');
      const elseE = parseExpr();
      return { kind: 'tern', cond, then: thenE, els: elseE };
    }
    return cond;
  }
  function parseOr(): AstNode {
    let left = parseAnd();
    while (eat('op', '||')) left = { kind: 'bin', op: '||', left, right: parseAnd() };
    return left;
  }
  function parseAnd(): AstNode {
    let left = parseEq();
    while (eat('op', '&&')) left = { kind: 'bin', op: '&&', left, right: parseEq() };
    return left;
  }
  function parseEq(): AstNode {
    let left = parseCmp();
    for (;;) {
      if (eat('op', '==')) left = { kind: 'bin', op: '==', left, right: parseCmp() };
      else if (eat('op', '!=')) left = { kind: 'bin', op: '!=', left, right: parseCmp() };
      else break;
    }
    return left;
  }
  function parseCmp(): AstNode {
    let left = parseAdd();
    for (;;) {
      if (eat('op', '<=')) left = { kind: 'bin', op: '<=', left, right: parseAdd() };
      else if (eat('op', '>=')) left = { kind: 'bin', op: '>=', left, right: parseAdd() };
      else if (eat('op', '<')) left = { kind: 'bin', op: '<', left, right: parseAdd() };
      else if (eat('op', '>')) left = { kind: 'bin', op: '>', left, right: parseAdd() };
      else break;
    }
    return left;
  }
  function parseAdd(): AstNode {
    let left = parseMul();
    for (;;) {
      if (eat('op', '+')) left = { kind: 'bin', op: '+', left, right: parseMul() };
      else if (eat('op', '-')) left = { kind: 'bin', op: '-', left, right: parseMul() };
      else break;
    }
    return left;
  }
  function parseMul(): AstNode {
    let left = parseUnary();
    for (;;) {
      if (eat('op', '*')) left = { kind: 'bin', op: '*', left, right: parseUnary() };
      else if (eat('op', '/')) left = { kind: 'bin', op: '/', left, right: parseUnary() };
      else if (eat('op', '%')) left = { kind: 'bin', op: '%', left, right: parseUnary() };
      else break;
    }
    return left;
  }
  function parseUnary(): AstNode {
    if (eat('op', '!')) return { kind: 'un', op: '!', arg: parseUnary() };
    if (eat('op', '-')) return { kind: 'un', op: '-', arg: parseUnary() };
    return parseMember();
  }
  function parseMember(): AstNode {
    let node = parsePrimary();
    // Postfix-ketju: .id / [expr] / (args) kunnes mikään ei osu,
    // jotta a.b[0].c() jäsentyy yhdellä kierrolla.
    for (;;) {
      if (eat('op', '.')) {
        const ident = expect('id') as { value: string };
        node = { kind: 'member', obj: node, prop: guardIdentifier(ident.value) };
      } else if (eat('op', '[')) {
        const keyExpr = parseExpr();
        expect('op', ']');
        node = { kind: 'index', obj: node, key: keyExpr };
      } else if (eat('op', '(')) {
        const args: AstNode[] = [];
        const n = peek();
        if (!(n && n.type === 'op' && n.value === ')')) {
          args.push(parseExpr());
          while (eat('op', ',')) args.push(parseExpr());
        }
        expect('op', ')');
        node = { kind: 'call', callee: node, args };
      } else {
        break;
      }
    }
    return node;
  }
  function parsePrimary(): AstNode {
    const t = peek();
    if (!t) throw new Error('Unexpected end of expression');
    if (t.type === 'num' || t.type === 'str' || t.type === 'bool') {
      pos++;
      return { kind: 'lit', value: t.value };
    }
    if (t.type === 'null') {
      pos++;
      return { kind: 'lit', value: null };
    }
    if (t.type === 'id') {
      pos++;
      return { kind: 'id', name: guardIdentifier(t.value) };
    }
    if (t.type === 'op' && t.value === '(') {
      pos++;
      const inner = parseExpr();
      expect('op', ')');
      return inner;
    }
    if (t.type === 'op' && t.value === '[') {
      pos++;
      const elems: AstNode[] = [];
      const n = peek();
      if (!(n && n.type === 'op' && n.value === ']')) {
        elems.push(parseExpr());
        while (eat('op', ',')) elems.push(parseExpr());
      }
      expect('op', ']');
      return { kind: 'array', elements: elems };
    }
    if (t.type === 'op' && t.value === '{') {
      pos++;
      const pairs: { key: string; value: AstNode }[] = [];
      const n = peek();
      if (!(n && n.type === 'op' && n.value === '}')) {
        do {
          // Avaimet ovat tunnisteita tai merkkijonoliteraaleja — laskettuja
          // avaimia ei tarvita käyttötapauksissa.
          const keyTok = peek();
          let keyName: string;
          if (keyTok && keyTok.type === 'id') {
            keyName = keyTok.value;
            pos++;
          } else if (keyTok && keyTok.type === 'str') {
            keyName = keyTok.value;
            pos++;
          } else {
            throw new Error('Expected object key');
          }
          expect('op', ':');
          pairs.push({ key: keyName, value: parseExpr() });
        } while (eat('op', ','));
      }
      expect('op', '}');
      return { kind: 'object', pairs };
    }
    throw new Error(`Unexpected token: ${JSON.stringify(t)}`);
  }

  const root = parseExpr();
  if (pos < tokens.length) {
    throw new Error(`Trailing tokens at position ${pos}`);
  }
  return root;
}

// ---------- Riippuvuuskeräin ----------

/**
 * Kävelee ASTin ja kerää kaikki "id.slot"-parit jotka lauseke lukee, jotta
 * runtime voi tilata jokaisen bindingin täsmälleen omille syötteilleen.
 * Arrow-runko ylikerää (parametrinimet voivat osua tilan avaimiin) —
 * hinta on yksi ylimääräinen re-eval, tulos on silti oikea.
 */
export function collectDeps(ast: AstNode, deps: Record<string, true> = {}): Record<string, true> {
  if (!ast) return deps;
  switch (ast.kind) {
    case 'member':
      if (ast.obj.kind === 'id') {
        deps[`${ast.obj.name}.${ast.prop}`] = true;
        return deps;
      }
      collectDeps(ast.obj, deps);
      return deps;
    case 'index':
      collectDeps(ast.obj, deps);
      collectDeps(ast.key, deps);
      return deps;
    case 'call':
      collectDeps(ast.callee, deps);
      for (const a of ast.args) collectDeps(a, deps);
      return deps;
    case 'array':
      for (const e of ast.elements) collectDeps(e, deps);
      return deps;
    case 'object':
      for (const p of ast.pairs) collectDeps(p.value, deps);
      return deps;
    case 'arrow':
      collectDeps(ast.body, deps);
      return deps;
    case 'bin':
      collectDeps(ast.left, deps);
      collectDeps(ast.right, deps);
      return deps;
    case 'un':
      collectDeps(ast.arg, deps);
      return deps;
    case 'tern':
      collectDeps(ast.cond, deps);
      collectDeps(ast.then, deps);
      collectDeps(ast.els, deps);
      return deps;
    default:
      return deps;
  }
}

// ---------- Nimiavaruuden uudelleenkirjoitus (WP-HV5) ----------

/**
 * Palauta AST jossa vapaat tunnisteet on korvattu `mapping`in mukaan.
 *
 * <p>Tätä tarvitaan `repeater`ille: sama lapsikomponentti renderöityy kerran
 * per rivi, joten sen `data-app-id` skoopataan (`total` → `total__r2`) ettei
 * elävässä DOMissa ole kahta samaa id:tä. Silloin myös rivin sidontojen on
 * osoitettava skoopattuun id:hen — muuten rivin 2 lauseke lukisi rivin 0
 * arvoja. Rividata näkyy samalla mekanismilla nimellä `row`, joka mäpätään
 * rivikohtaiseen tilaobjektiin.
 *
 * <p>Uudelleenkirjoitus tehdään ASTille eikä lähdetekstille tarkoituksella:
 * merkkijonokorvaus osuisi myös merkkijonoliteraaleihin ja property-nimiin
 * (`x.total`), joissa `total` ei ole tunniste vaan avain. Tässä korvataan vain
 * `id`-solmut — `member`in `prop` on merkkijonokenttä eikä osu.
 *
 * <p>Arrow-parametri VARJOSTAA mäppäyksen omassa rungossaan: `rows.map(row =>
 * row.x)` viittaa lambdan parametriin eikä rivin dataan, ja sen kaappaaminen
 * rikkoisi täysin laillisen lausekkeen.
 */
export function rewriteScopedIds(
  ast: AstNode,
  mapping: ReadonlyMap<string, string>,
): AstNode {
  if (!ast || mapping.size === 0) return ast;
  switch (ast.kind) {
    case 'id': {
      const next = mapping.get(ast.name);
      return next === undefined ? ast : { kind: 'id', name: next };
    }
    case 'member':
      return { kind: 'member', obj: rewriteScopedIds(ast.obj, mapping), prop: ast.prop };
    case 'index':
      return {
        kind: 'index',
        obj: rewriteScopedIds(ast.obj, mapping),
        key: rewriteScopedIds(ast.key, mapping),
      };
    case 'call':
      return {
        kind: 'call',
        callee: rewriteScopedIds(ast.callee, mapping),
        args: ast.args.map((a) => rewriteScopedIds(a, mapping)),
      };
    case 'un':
      return { kind: 'un', op: ast.op, arg: rewriteScopedIds(ast.arg, mapping) };
    case 'bin':
      return {
        kind: 'bin',
        op: ast.op,
        left: rewriteScopedIds(ast.left, mapping),
        right: rewriteScopedIds(ast.right, mapping),
      };
    case 'tern':
      return {
        kind: 'tern',
        cond: rewriteScopedIds(ast.cond, mapping),
        then: rewriteScopedIds(ast.then, mapping),
        els: rewriteScopedIds(ast.els, mapping),
      };
    case 'array':
      return { kind: 'array', elements: ast.elements.map((e) => rewriteScopedIds(e, mapping)) };
    case 'object':
      return {
        kind: 'object',
        pairs: ast.pairs.map((p) => ({ key: p.key, value: rewriteScopedIds(p.value, mapping) })),
      };
    case 'arrow': {
      // Varjostus: parametrinimi voittaa mäppäyksen rungon sisällä.
      const shadowed = ast.params.filter((p) => mapping.has(p));
      if (shadowed.length === 0) {
        return { kind: 'arrow', params: ast.params, body: rewriteScopedIds(ast.body, mapping) };
      }
      const inner = new Map(mapping);
      for (const p of shadowed) inner.delete(p);
      return { kind: 'arrow', params: ast.params, body: rewriteScopedIds(ast.body, inner) };
    }
    default:
      return ast;
  }
}

// ---------- Evaluaattori ----------

/**
 * Metodinimet jotka on turvallista kutsua käyttäjäarvoilla — vain puhtaita/
 * lukevia metodeja (sort/reverse/push yms. mutatoivat ja puuttuvat).
 * Turvakorjaus 1: Set, jonka jäsenyystesti ei näe prototyyppiketjua —
 * lähteen objektiliteraali vuoti SAFE_METHODS['constructor'] → truthy.
 */
const SAFE_METHODS: ReadonlySet<string> = new Set([
  // string
  'trim', 'trimStart', 'trimEnd', 'toLowerCase', 'toUpperCase',
  'slice', 'substring', 'charAt', 'indexOf', 'lastIndexOf',
  'includes', 'startsWith', 'endsWith', 'split', 'concat', 'repeat',
  'padStart', 'padEnd',
  // array (vain immutable)
  'join', 'filter', 'map', 'some', 'every', 'find', 'findIndex',
  'reduce', 'flat',
  // number
  'toFixed', 'toString',
]);

/** Vastaanottimettomat funktiot/nimiavaruudet joita lauseke saa kutsua. */
const SAFE_GLOBALS: Record<string, unknown> = {
  Number,
  String,
  Boolean,
  parseInt: (s: string) => parseInt(s, 10),
  parseFloat,
  isNaN,
  isFinite,
  Math: {
    min: Math.min, max: Math.max,
    floor: Math.floor, ceil: Math.ceil, round: Math.round,
    abs: Math.abs, sqrt: Math.sqrt, pow: Math.pow,
    PI: Math.PI, E: Math.E,
  },
  // Aika ja päivämäärät (WP-HV4). ISO-merkkijonot sisään ja ulos: `Date`-objekti
  // EI tule kieleen, joten SAFE_METHODS-porttia ei tarvitse avata. Virheellinen
  // syöte palauttaa undefinedin, joka putoaa sidonnan fallbackiin.
  // `now()` ei tikitä — tikittävä lukema tulee `clock`-komponentista (D4 = C).
  today: todayLocal,
  now: nowLocal,
  daysBetween: daysBetweenSigned,
  addDays: addDaysTo,
  formatDate: formatDateLocalized,
};

/**
 * SAFE_GLOBALSin nimiavaruusobjektit (Math): niiden omat funktiot saa
 * kutsua ilman SAFE_METHODS-porttia — portti koskee käyttäjäarvojen
 * metodeja. (Lähteessä Math.max(1,2) palautti undefined, koska
 * metodiportti sovellettiin myös SAFE_GLOBALS-jäseniin — korjattu tässä;
 * kielioppikorpus pinnaa käyttäytymisen.)
 */
const SAFE_NAMESPACE_OBJECTS: ReadonlySet<object> = new Set([SAFE_GLOBALS.Math as object]);

const hasOwn = (obj: object, key: string) => Object.prototype.hasOwnProperty.call(obj, key);

/**
 * Turvakorjaus 2: property-luku joka estää prototyyppiketjun tunnisteet
 * myös laskettuna avaimena. Kaikki member/index/metodi-polut kulkevat
 * tästä.
 */
function readProp(obj: unknown, key: unknown): unknown {
  if (obj == null) return undefined;
  const k = String(key);
  if (FORBIDDEN_IDENTIFIERS.has(k)) return undefined;
  return (obj as Record<string, unknown>)[k];
}

export function evalAst(ast: AstNode, state: ExprState): unknown {
  switch (ast.kind) {
    case 'lit':
      return ast.value;
    case 'id':
      // Paljas tunniste: ensin reaktiivinen tila (komponentti-id:t /
      // vars / params), sitten SAFE_GLOBALS (Number/Math/...).
      if (hasOwn(state, ast.name)) return state[ast.name];
      return hasOwn(SAFE_GLOBALS, ast.name) ? SAFE_GLOBALS[ast.name] : undefined;
    case 'member':
      return readProp(evalAst(ast.obj, state), ast.prop);
    case 'index':
      return readProp(evalAst(ast.obj, state), evalAst(ast.key, state));
    case 'call': {
      const args = ast.args.map((a) => evalAst(a, state));
      // Metodikutsu: callee on member-lauseke (esim. str.trim). Vastaanotin
      // + metodinimi ratkaistaan, nimi portitetaan SAFE_METHODSin läpi
      // (Set.has — ei prototyyppivuotoa), sitten kutsutaan.
      if (ast.callee.kind === 'member') {
        const receiver = evalAst(ast.callee.obj, state);
        if (receiver == null) return undefined;
        const name = ast.callee.prop;
        const isSafeNamespace = typeof receiver === 'object'
          && SAFE_NAMESPACE_OBJECTS.has(receiver as object)
          && hasOwn(receiver as object, name);
        if (!isSafeNamespace && !SAFE_METHODS.has(name)) return undefined;
        const fn = readProp(receiver, name);
        if (typeof fn !== 'function') return undefined;
        try {
          return fn.apply(receiver, args);
        } catch {
          return undefined;
        }
      }
      // Paljas kutsu: callee resolvautuu funktioon (SAFE_GLOBALS-jäsen).
      const fnRoot = evalAst(ast.callee, state);
      if (typeof fnRoot !== 'function') return undefined;
      try {
        return fnRoot.apply(undefined, args);
      } catch {
        return undefined;
      }
    }
    case 'un': {
      const v = evalAst(ast.arg, state);
      if (ast.op === '!') return !v;
      return -(v as number);
    }
    case 'bin': {
      if (ast.op === '&&') return evalAst(ast.left, state) && evalAst(ast.right, state);
      if (ast.op === '||') return evalAst(ast.left, state) || evalAst(ast.right, state);
      const l = evalAst(ast.left, state) as never;
      const r = evalAst(ast.right, state) as never;
      switch (ast.op) {
        case '+': return (l as number) + (r as number);
        case '-': return (l as number) - (r as number);
        case '*': return (l as number) * (r as number);
        case '/': return (l as number) / (r as number);
        case '%': return (l as number) % (r as number);
        case '==': return l === r;
        case '!=': return l !== r;
        case '<': return l < r;
        case '>': return l > r;
        case '<=': return l <= r;
        case '>=': return l >= r;
        default: return undefined;
      }
    }
    case 'tern':
      return evalAst(ast.cond, state) ? evalAst(ast.then, state) : evalAst(ast.els, state);
    case 'array':
      return ast.elements.map((e) => evalAst(e, state));
    case 'object': {
      const obj: Record<string, unknown> = {};
      for (const p of ast.pairs) obj[p.key] = evalAst(p.value, state);
      return obj;
    }
    case 'arrow': {
      // Parametrit + runko + ulompi tila kaapataan; palautettu funktio
      // evaluoi rungon laajennettua tilaa vasten (param → argumentti).
      // Näin arr.filter(x => x.active) toimii.
      const { params, body } = ast;
      return (...callArgs: unknown[]) => {
        const localState: ExprState = { ...state };
        params.forEach((p, idx) => {
          localState[p] = callArgs[idx];
        });
        try {
          return evalAst(body, localState);
        } catch {
          return undefined;
        }
      };
    }
    default:
      return undefined;
  }
}

/**
 * Jäsennä + evaluoi lauseke tilaa vasten. Jäsennys- tai evaluointivirhe →
 * undefined (binding degradoituu fallback-arvoonsa, sivu ei kaadu).
 */
export function evalExpression(src: string, state: ExprState): unknown {
  try {
    return evalAst(parse(src), state);
  } catch {
    return undefined;
  }
}

// ---------- Serialisointi (AST → lähdeteksti) ----------

function quoteString(s: string): string {
  return `'${s.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
}

/**
 * Serialisoi AST takaisin lausekelähteeksi. Käytetään AST-pohjaisessa
 * tunnisteiden uudelleenkirjoituksessa (AB-WP3.1): parse → muokkaa
 * identifier-nodet → serialisoi, jolloin merkkijonoliteraalit EIVÄT muutu
 * (lähteen regex-rename sotki ne). Ei tuota täsmälleen alkuperäistä
 * whitespacea, mutta on semanttisesti ekvivalentti ja parse:autuu takaisin
 * samaksi ASTiksi.
 */
export function serialize(ast: AstNode): string {
  switch (ast.kind) {
    case 'lit':
      if (typeof ast.value === 'string') return quoteString(ast.value);
      if (ast.value === null) return 'null';
      return String(ast.value);
    case 'id':
      return ast.name;
    case 'member':
      return `${serialize(ast.obj)}.${ast.prop}`;
    case 'index':
      return `${serialize(ast.obj)}[${serialize(ast.key)}]`;
    case 'call':
      return `${serialize(ast.callee)}(${ast.args.map(serialize).join(', ')})`;
    case 'un':
      return `${ast.op}${serialize(ast.arg)}`;
    case 'bin':
      return `(${serialize(ast.left)} ${ast.op} ${serialize(ast.right)})`;
    case 'tern':
      return `(${serialize(ast.cond)} ? ${serialize(ast.then)} : ${serialize(ast.els)})`;
    case 'array':
      return `[${ast.elements.map(serialize).join(', ')}]`;
    case 'object':
      return `{${ast.pairs.map((p) => `${quoteString(p.key)}: ${serialize(p.value)}`).join(', ')}}`;
    case 'arrow':
      // Sulkeet aina kun parametreja ei ole täsmälleen yksi: ilman niitä
      // kahden parametrin nuoli serialisoituisi muotoon `a, b => …`, joka ei
      // jäsenny takaisin — ja renameIdentifierInExpression rikkoisi juuri ne
      // lausekkeet joita sulkullinen parametrilista tekee mahdollisiksi.
      return ast.params.length === 1
        ? `${ast.params[0]} => ${serialize(ast.body)}`
        : `(${ast.params.join(', ')}) => ${serialize(ast.body)}`;
    default:
      return '';
  }
}

/**
 * Kirjoita jokainen paljas tunniste {@code oldId} muotoon {@code newId}
 * AST-tasolla ja serialisoi takaisin. Vain identifier-nodet (komponentti-
 * viittaukset kuten {@code oldId.value}) muuttuvat — merkkijonoliteraali
 * {@code 'oldId'} lausekkeen sisällä pysyy koskemattomana. Palauttaa
 * alkuperäisen tekstin muuttumattomana, jos lauseke ei parse:audu (ei
 * riko rikkinäistä lauseketta rename-operaatiossa).
 */
export function renameIdentifierInExpression(expr: string, oldId: string, newId: string): string {
  let ast: AstNode;
  try {
    ast = parse(expr);
  } catch {
    return expr;
  }
  let changed = false;
  const walk = (node: AstNode): void => {
    switch (node.kind) {
      case 'id':
        if (node.name === oldId) {
          node.name = newId;
          changed = true;
        }
        return;
      case 'member':
        walk(node.obj);
        return;
      case 'index':
        walk(node.obj);
        walk(node.key);
        return;
      case 'call':
        walk(node.callee);
        node.args.forEach(walk);
        return;
      case 'un':
        walk(node.arg);
        return;
      case 'bin':
        walk(node.left);
        walk(node.right);
        return;
      case 'tern':
        walk(node.cond);
        walk(node.then);
        walk(node.els);
        return;
      case 'array':
        node.elements.forEach(walk);
        return;
      case 'object':
        node.pairs.forEach((p) => walk(p.value));
        return;
      case 'arrow':
        // Arrow-parametri varjostaa ulomman nimen — jos param === oldId,
        // rungon viittaukset osoittavat parametriin, ei komponenttiin.
        if (node.params.includes(oldId)) return;
        walk(node.body);
        return;
      default:
    }
  };
  walk(ast);
  return changed ? serialize(ast) : expr;
}
